// Native HubSpot/HighLevel adapters and CRM writeback (PRD §15).
import { z } from 'zod';
import * as C from './api-crm.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

const Conn = W({ connectionId: IdParam });

export const crmRoutes = {
  listCrmConnections: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections', summary: 'Conexiones de CRM (HubSpot/HighLevel) con mapeo y capacidades', tag: 'crm',
    minRole: 'manager', params: Ws, response: items(C.CrmConnectionSchema),
  }),
  startCrmOauth: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/oauth/start', summary: 'Iniciar autorización OAuth del CRM (state ligado a la sesión)', tag: 'crm',
    minRole: 'admin', params: Ws, body: C.StartCrmOauthSchema, response: C.StartCrmOauthResultSchema,
  }),
  // Browser redirect from the provider. Public (no Origin), but the state is bound to the SAME session + workspace.
  crmOauthCallback: route({
    method: 'GET', path: '/crm/oauth/:provider/callback', summary: 'Retorno OAuth del CRM (redirige a la aplicación)', tag: 'crm',
    public: 'auth', params: z.object({ provider: C.CrmProviderSchema }), query: C.CrmOauthCallbackQuerySchema, response: C.CrmOauthCallbackResponseSchema,
  }),
  createCrmTokenConnection: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/token', summary: 'Conectar CRM con token privado (validado en el servidor)', tag: 'crm',
    minRole: 'admin', params: Ws, body: C.CreateCrmTokenConnectionSchema, response: C.CrmConnectResultSchema, status: 201,
  }),
  rotateCrmInboundToken: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/:connectionId/inbound-token', summary: 'Rotar el token de recepción de webhooks del CRM', tag: 'crm',
    minRole: 'admin', params: Conn, response: C.CrmConnectResultSchema,
  }),
  getCrmMapping: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections/:connectionId/mapping', summary: 'Mapeo de proyectos, responsables, calificación, etapas e inventario', tag: 'crm',
    minRole: 'manager', params: Conn, response: C.CrmMappingSchema,
  }),
  updateCrmMapping: route({
    method: 'PUT', path: '/workspaces/:workspaceId/crm/connections/:connectionId/mapping', summary: 'Guardar mapeo del CRM', tag: 'crm',
    minRole: 'admin', params: Conn, body: C.CrmMappingSchema, response: C.CrmConnectionSchema,
  }),
  listCrmSamples: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections/:connectionId/samples', summary: 'Eventos capturados del CRM (para vista previa del mapeo)', tag: 'crm',
    minRole: 'admin', params: Conn, response: items(C.CrmSampleSchema),
  }),
  previewCrmMapping: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/:connectionId/preview', summary: 'Vista previa / ejecución en seco del mapeo con un payload de muestra', tag: 'crm',
    minRole: 'admin', params: Conn, body: C.CrmPreviewRequestSchema, response: C.CrmPreviewResultSchema,
  }),
  checkCrmConnection: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/:connectionId/check', summary: 'Revisión de salud de la conexión del CRM', tag: 'crm',
    minRole: 'admin', params: Conn, response: C.CrmHealthSchema,
  }),
  listSyncFailures: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/sync-failures', summary: 'Errores de sincronización con el CRM', tag: 'crm',
    minRole: 'manager', params: Ws, query: C.SyncFailureListQuerySchema, response: items(C.SyncFailureSchema),
  }),
  retrySyncFailure: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/sync-failures/:failureId/retry', summary: 'Reintentar la escritura en el CRM (misma identidad de evento)', tag: 'crm',
    minRole: 'admin', params: W({ failureId: IdParam }), response: C.SyncFailureSchema,
  }),
  resolveSyncFailure: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/sync-failures/:failureId/resolve', summary: 'Marcar error de sincronización como resuelto', tag: 'crm',
    minRole: 'admin', params: W({ failureId: IdParam }), response: C.SyncFailureSchema,
  }),

  getHighLevelWhatsApp: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp', summary: 'WhatsApp a través del número conectado en HighLevel', tag: 'crm',
    module: 'whatsapp', minRole: 'manager', params: Conn, response: C.HighLevelWhatsAppStateSchema,
  }),
  enableHighLevelWhatsApp: route({
    method: 'PUT', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp', summary: 'Usar el WhatsApp conectado en HighLevel (sin verificar hasta una prueba real)', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, body: C.EnableHighLevelWhatsAppSchema, response: C.HighLevelWhatsAppStateSchema,
  }),
  disableHighLevelWhatsApp: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp', summary: 'Dejar de usar el WhatsApp de HighLevel', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, response: C.HighLevelWhatsAppStateSchema,
  }),
  saveHighLevelWhatsAppTemplates: route({
    method: 'PUT', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp/templates', summary: 'Plantillas aprobadas de WhatsApp con su id en HighLevel', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, body: C.SaveHighLevelTemplatesSchema, response: C.HighLevelWhatsAppStateSchema,
  }),
  testHighLevelWhatsApp: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp/test', summary: 'Prueba de envío por el WhatsApp de HighLevel', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, body: C.HighLevelWhatsAppTestSchema, response: C.HighLevelWhatsAppStateSchema,
  }),
  confirmHighLevelWhatsAppTest: route({
    method: 'POST', path: '/workspaces/:workspaceId/crm/connections/:connectionId/whatsapp/test/confirm', summary: 'Confirmar que la prueba de envío llegó al teléfono', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, response: C.HighLevelWhatsAppStateSchema,
  }),

  getHubspotInbox: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections/:connectionId/inbox', summary: 'Puente de WhatsApp hacia la bandeja de HubSpot', tag: 'crm',
    module: 'whatsapp', minRole: 'manager', params: Conn, response: C.HubspotInboxStateSchema,
  }),
  listHubspotInboxes: route({
    method: 'GET', path: '/workspaces/:workspaceId/crm/connections/:connectionId/inbox/inboxes', summary: 'Bandejas de conversaciones del portal de HubSpot', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, response: C.HubspotInboxListSchema,
  }),
  enableHubspotInbox: route({
    method: 'PUT', path: '/workspaces/:workspaceId/crm/connections/:connectionId/inbox', summary: 'Conectar un número de WhatsApp a una bandeja de HubSpot (canal personalizado)', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, body: C.EnableHubspotInboxSchema, response: C.HubspotInboxStateSchema,
  }),
  disableHubspotInbox: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/crm/connections/:connectionId/inbox', summary: 'Dejar de reflejar WhatsApp en la bandeja de HubSpot', tag: 'crm',
    module: 'whatsapp', minRole: 'admin', params: Conn, response: C.HubspotInboxStateSchema,
  }),

  // Machine routes: provider → us. `:connectionId` may be `app` for app-level OAuth subscriptions (routed by account id).
  hubspotWebhook: route({
    method: 'POST', path: '/providers/hubspot/:connectionId/webhooks', summary: 'Webhook de HubSpot (X-HubSpot-Signature-v3 o token de conexión)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: C.CrmWebhookAckSchema,
  }),
  hubspotChannelWebhook: route({
    method: 'POST', path: '/providers/hubspot-channel/webhooks', summary: 'Mensajes escritos en la bandeja de HubSpot (canal personalizado; X-HubSpot-Signature-v3)', tag: 'ingress',
    public: 'ingress', response: C.CrmWebhookAckSchema,
  }),
  highlevelWebhook: route({
    method: 'POST', path: '/providers/highlevel/:connectionId/webhooks', summary: 'Webhook de HighLevel (X-GHL-Signature Ed25519 o token de conexión)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: C.CrmWebhookAckSchema,
  }),
} as const;
